import type { Config } from "../state/config";
import { MOT_BIENVEILLANT } from "./prompt";
import { chatRequest, reasoningParams } from "./rcpClient";

// Titre court d'une conversation (Figma 11 : « Congrès à Vienne : frais
// remboursables »), dans la langue de l'interface, rédigé par le petit modèle
// de reformulation après la première réponse. Sans lui, la liste montrait le
// début de la question, tronqué, et plusieurs conversations s'y ressemblaient.

const TITLE_TIMEOUT_MS = 20_000;
const MAX_LENGTH = 70;

function system(locale: "fr" | "en"): string {
  const lang = locale === "en" ? "anglais" : "français";
  return `Tu donnes un titre court à une conversation, pour la liste de l'historique.

Règles :
- En ${lang}, quelle que soit la langue de la question.
- 3 à 7 mots, qui disent le sujet concret : « Congrès à Vienne : frais remboursables », « Congé sans solde : formulaire », « Contrat de sous-traitance : analyse LPD ».
- Pas de nom de personne, d'adresse e-mail ni de numéro de téléphone.
- Pas de guillemets, pas de point final, pas de préambule : le titre seul.

${MOT_BIENVEILLANT}`;
}

export function cleanTitle(raw: string): string | null {
  let s = raw.split("\n").find((l) => l.trim())?.trim() ?? "";
  s = s.replace(/^(titre|title)\s*:\s*/i, "").trim();
  // Point final, guillemets, puis un point resté dedans : « Titre ». ou « Titre. »
  s = s.replace(/[.。]$/, "").trim();
  s = s.replace(/^[«"'“]\s*|\s*[»"'”]$/g, "").trim();
  s = s.replace(/[.。]$/, "").trim();
  if (s.length < 3) return null;
  return s.length > MAX_LENGTH ? `${s.slice(0, MAX_LENGTH).trimEnd()}…` : s;
}

/** Le titre, ou null si le modèle ne répond pas : le titre provisoire reste. */
export async function generateTitle(
  question: string,
  answer: string,
  cfg: Config,
  locale: "fr" | "en",
): Promise<string | null> {
  if (!cfg.rewriteModel) return null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TITLE_TIMEOUT_MS);
  try {
    const data = await chatRequest(
      {
        model: cfg.rewriteModel,
        max_tokens: 200,
        ...reasoningParams(cfg.rewriteModel, "coupe"),
        temperature: 0,
        messages: [
          { role: "system", content: system(locale) },
          {
            role: "user",
            content: `Question : ${question.slice(0, 1500)}\n\nDébut de la réponse : ${answer.slice(0, 600)}\n\nTitre :`,
          },
        ],
      },
      cfg,
      controller.signal,
    );
    const raw = data.choices?.[0]?.message?.content;
    return typeof raw === "string" ? cleanTitle(raw) : null;
  } catch (err) {
    console.warn("[conversationTitle] failed:", err);
    return null;
  } finally {
    clearTimeout(timeout);
  }
}
